import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import {
  useCreateItemLabel,
  useUpdateItemLabel,
  type ItemLabel,
} from "@/api/items";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import { badgeColors, type TagColor } from "./types";

interface Props {
  /** The tag being edited, or null for a new one. */
  tag: ItemLabel | null;
  open: boolean;
  onClose: () => void;
}

function randomColor(): TagColor {
  const colors = Object.keys(badgeColors) as TagColor[];
  return colors[Math.floor(Math.random() * colors.length)];
}

export function EditTagDialog({ tag, open, onClose }: Props) {
  const { t } = useTranslation();
  const create = useCreateItemLabel();
  const update = useUpdateItemLabel();

  const [name, setName] = useState("");
  const [color, setColor] = useState<TagColor>(randomColor);

  useEffect(() => {
    if (!open) return;
    setName(tag?.name ?? "");
    setColor((tag?.color as TagColor) ?? randomColor());
  }, [tag, open]);

  const pending = create.isPending || update.isPending;

  async function save() {
    if (tag) await update.mutateAsync({ id: tag.id, name, color });
    else await create.mutateAsync({ name, color });
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {tag ? t("settings.tags.editTitle") : t("settings.tags.create")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="tag-name">{t("settings.tags.nameLabel")}</Label>
            <Input
              id="tag-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>{t("settings.tags.colorLabel")}</Label>
            <div
              role="radiogroup"
              aria-label={t("settings.tags.colorLabel")}
              className="flex flex-wrap gap-2"
            >
              {(Object.keys(badgeColors) as TagColor[]).map((option) => (
                <button
                  key={option}
                  type="button"
                  role="radio"
                  aria-checked={color === option}
                  aria-label={option}
                  onClick={() => setColor(option)}
                  className={cn(
                    "size-8 rounded-full border-2",
                    badgeColors[option],
                    color === option
                      ? "border-foreground"
                      : "border-transparent hover:shadow",
                  )}
                />
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("common.buttons.cancel")}
          </Button>
          <Button disabled={!name || pending} onClick={save}>
            {t("common.buttons.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
